import { sleep } from "./sleep";

type PromiseWithRetryOptions<T> = {
  onRetry?: (error: Error, attempt: number) => void;
  baseDelay?: number; // Base delay in ms (default: 1000)
  backoffMultiplier?: number; // Exponential backoff multiplier (default: 2)
  maxDelay?: number; // Maximum delay cap in ms (default: 30000)
  jitter?: boolean; // Add randomness to prevent thundering herd (default: true)
};

/**
 * Attempts to execute an asynchronous function multiple times with exponential backoff delays between retries.
 * Works like regular async/await - returns data on success, throws error on failure.
 *
 * @param asyncFn The asynchronous function to execute.
 * @param maxRetries The maximum number of retry attempts.
 * @param options Optional callbacks and configuration for exponential backoff.
 * @returns A promise that resolves with the result of the async function if successful, or throws if all retries fail.
 */
export async function promiseWithRetry<T>(
  asyncFn: () => Promise<T>,
  maxRetries: number,
  options: PromiseWithRetryOptions<T> = {}
): Promise<T> {
  const {
    onRetry = () => {},
    baseDelay = 1000, // Default 1 second base delay
    backoffMultiplier = 2, // Default exponential backoff multiplier
    maxDelay = 30000, // Default max delay 30 seconds
    jitter = true, // Default add jitter
  } = options;

  let lastError: Error;
  let attempt = 0;
  
  while (attempt <= maxRetries) {
    try {
      const data = await asyncFn();
      return data;
    } catch (error: any) {
      lastError = error;
      attempt++;
      console.error(`Attempt ${attempt} failed:`, error);
      
      if (attempt <= maxRetries) {
        onRetry(error, attempt);
      }

      if (attempt > maxRetries) {
        console.error('Max retries reached. Failing.');
        throw lastError;
      }

      // Calculate exponential backoff delay
      const exponentialDelay = baseDelay * Math.pow(backoffMultiplier, attempt - 1);
      const cappedDelay = Math.min(exponentialDelay, maxDelay);
      
      // Add jitter (randomness) to prevent thundering herd problem
      const finalDelay = jitter 
        ? cappedDelay * (0.5 + Math.random() * 0.5) // Random between 50% and 100% of calculated delay
        : cappedDelay;

      console.log(`Retrying in ${Math.round(finalDelay)}ms (attempt ${attempt}/${maxRetries})`);
      
      // Wait before the next retry
      if (finalDelay > 0) {
        await sleep(finalDelay);
      }
    }
  }
  
  // This should never be reached, but TypeScript requires it
  throw lastError!;
} 